import { useEffect, useState, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { OnboardingFormData } from "../OnboardingPage";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks";
import { fetchPersonaCommunitiesAggregateData } from "../../../stores/thunks/discoverCommunitiesThunk";
import { clearMatchedCreators } from "../../../stores/slice/discoverCommunitiesSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { PersonaAPIResponseData } from "../../../stores/types/personasTypes.tsx";
import { fetchPersonasData } from "../../../stores/thunks/personasThunk";
import {
  pollForPersonaCountChange,
  pollForOnboardingSuccess,
  pollForCreatorAggregate,
} from "../../../helpers/services/pollingService";
import {
  setHasGeneratedAIPersonas,
  setOnboardingResult,
} from "../../../stores/slice/onboardingSlice.tsx";
import {
  RevButton,
  RevCard,
  RevDialog,
  RevPersonaCard,
  RevSpinner,
} from "@revmatics/catalyst";
import { CreatorData } from "../../../stores/types/creatorsTypes.tsx";
import { PersonaDetail } from "../../../components/layout/components/globalSlideouts/components/persona/PersonaDetail.tsx";
import { CreatorCard } from "../../../components/layout/components/globalSlideouts/components/creator/components/creatorCard/CreatorCard.tsx";
import { CreatorOverview } from "../../../components/layout/components/globalSlideouts/components/creator/CreatorOverview.tsx";

export const Step3_CreatorDiscovery = () => {
  const [viewingPersona, setViewingPersona] =
    useState<PersonaAPIResponseData | null>(null);
  const [viewingCreator, setViewingCreator] = useState<CreatorData | null>(
    null,
  );
  const { watch, setValue } = useFormContext<OnboardingFormData>();
  const dispatch = useAppDispatch();
  const {
    initialPersonaCount,
    hasGeneratedAIPersonas,
    taskId,
    campaign,
    briefStatuses,
  } = useAppSelector((state) => state.onboarding);
  const { personas: allPersonas, isLoadingList: isLoadingPersonas } =
    useAppSelector((state) => state.personas);
  const {
    aggregates: matchedCreatorsMap,
    loadingStates: isFetchingCreatorsMap,
  } = useAppSelector((state) => state.discoverCommunities);
  const { briefs } = useAppSelector((state) => state.creatorBriefs);

  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [isPollingPersonas, setIsPollingPersonas] = useState(
    !hasGeneratedAIPersonas,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
    useState<CreatorData | null>(null);
  const [isPollingCreators, setIsPollingCreators] = useState(false);
  const creatorPollingControllers = useRef(new Map<number, AbortController>());

  const selectedPersona = watch("selectedPersona", null);
  const selectedPairs = watch("selectedPairs", []);

  const selectedCreatorIds = useMemo(
    () => selectedPairs.map((pair) => pair.creatorId),
    [selectedPairs],
  );

  const allKnownCreators = useMemo(() => {
    return Object.values(matchedCreatorsMap).flatMap(
      (aggregate) => aggregate.top_creators,
    );
  }, [matchedCreatorsMap]);

  const availableCreators = useMemo(() => {
    if (!selectedPersona) {
      return [];
    }
    const creatorsForActivePersona =
      matchedCreatorsMap[selectedPersona]?.top_creators ?? [];

    return creatorsForActivePersona.filter(
      (creator) => !selectedCreatorIds.includes(creator.id),
    );
  }, [selectedPersona, matchedCreatorsMap, selectedCreatorIds]);

  useEffect(() => {
    if (!taskId || campaign) {
      setIsPollingStatus(false);
      return;
    }
    setIsPollingStatus(true);
    const controller = new AbortController();
    pollForOnboardingSuccess(taskId, { signal: controller.signal })
      .then((successfulResponse) => {
        if (successfulResponse.result) {
          dispatch(setOnboardingResult(successfulResponse.result));
          setIsPollingPersonas(true);
          setIsPollingStatus(false);
        } else {
          console.error("Polling succeeded but result object was missing.");
          setIsPollingStatus(false);
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Polling for onboarding status failed:", error);
        }
        setIsPollingStatus(false);
      });
    return () => controller.abort();
  }, [dispatch, taskId, campaign]);

  useEffect(() => {
    if (isPollingStatus || hasGeneratedAIPersonas || !taskId || !campaign) {
      setIsPollingPersonas(false);
      if (hasGeneratedAIPersonas && !isPollingStatus) {
        dispatch(fetchPersonasData());
      }
      return;
    }
    const controller = new AbortController();
    pollForPersonaCountChange(initialPersonaCount, 3, {
      signal: controller.signal,
    })
      .then(() => {
        dispatch(fetchPersonasData()).finally(() => {
          dispatch(setHasGeneratedAIPersonas());
          setIsPollingPersonas(false);
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Polling for new personas failed:", error);
        }
        setIsPollingPersonas(false);
      });
    return () => controller.abort();
  }, [
    dispatch,
    initialPersonaCount,
    hasGeneratedAIPersonas,
    taskId,
    isPollingStatus,
    campaign,
  ]);

  const personasToDisplay = useMemo(() => {
    if (!allPersonas || isPollingStatus || isPollingPersonas) return [];
    return [...allPersonas]
      .sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime(),
      )
      .slice(0, 3);
  }, [allPersonas, isPollingStatus, isPollingPersonas]);

  useEffect(() => {
    if (
      isPollingPersonas ||
      isPollingStatus ||
      personasToDisplay.length === 0
    ) {
      setIsPollingCreators(false);
      return;
    }

    const allLoadedInitially = personasToDisplay.every(
      (p) => matchedCreatorsMap[p.id]?.top_creators?.length > 0,
    );
    if (allLoadedInitially) {
      setIsPollingCreators(false);
      return;
    }

    setIsPollingCreators(true);
    const pollPromises: Promise<void>[] = [];
    const currentControllers = new Map<number, AbortController>();
    creatorPollingControllers.current = currentControllers;

    personasToDisplay.forEach((persona) => {
      if (!(matchedCreatorsMap[persona.id]?.top_creators?.length > 0)) {
        const controller = new AbortController();
        currentControllers.set(persona.id, controller);
        pollPromises.push(
          pollForCreatorAggregate(persona.id, {
            signal: controller.signal,
          }).then((aggregateData) => {
            dispatch(
              fetchPersonaCommunitiesAggregateData.fulfilled(
                { personaId: persona.id, data: aggregateData },
                "",
                { id: String(persona.id) },
              ),
            );
          }),
        );
      }
    });

    Promise.allSettled(pollPromises).then(() => {
      setIsPollingCreators(false);
    });

    return () => {
      currentControllers.forEach((controller) => controller.abort());
      creatorPollingControllers.current.clear();
    };
  }, [
    dispatch,
    isPollingPersonas,
    isPollingStatus,
    personasToDisplay,
    matchedCreatorsMap,
  ]);

  const handlePersonaSelect = (personaId: number) => {
    if (isPollingCreators) return;
    const isCurrentlySelected = selectedPersona === personaId;
    if (isCurrentlySelected) {
      setValue("selectedPersona", null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue("selectedPersona", personaId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleCreatorSelect = (creatorId: number) => {
    const isSelected = selectedCreatorIds.includes(creatorId);

    if (isSelected) {
      setValue(
        "selectedPairs",
        selectedPairs.filter((pair) => pair.creatorId !== creatorId),
        { shouldValidate: true, shouldDirty: true },
      );
    } else if (selectedPairs.length < 3 && selectedPersona) {
      const newPair = {
        personaId: selectedPersona,
        creatorId: creatorId,
      };
      setValue("selectedPairs", [...selectedPairs, newPair], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  useEffect(() => {
    if (!selectedPersona) {
      dispatch(clearMatchedCreators());
    }
  }, [selectedPersona, dispatch]);

  if (isLoadingPersonas && !allPersonas) {
    return <RevSpinner color="primary" centered size="medium" />;
  }

  const showInitialPollingUI = isPollingStatus || isPollingPersonas;

  return (
    <div>
      <RevCard
        title="Buyer personas"
        className="!px-4 !pb-4"
        paddingTitle="!pt-4 !px-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <p className="typography-body-xs text-monochrome-600">
            {showInitialPollingUI
              ? isPollingStatus
                ? "Checking setup status..."
                : "Generating AI personas. Analyzing 350M creators to find your best matches..."
              : isPollingCreators
                ? `Generating creators for ${personasToDisplay.length} personas...`
                : `We generated ${personasToDisplay.length} new customer personas. Select a persona to see matched creators.`}
          </p>
          {(!isPollingStatus && isPollingPersonas) || isPollingCreators ? (
            <RevSpinner color="primary" size="small" />
          ) : null}
        </div>

        {showInitialPollingUI ? (
          <div className="flex flex-col items-center justify-center text-center">
            <RevSpinner color="primary" centered size="large" />
            <p className="mt-4 text-monochrome-500">
              {isPollingStatus
                ? "Checking setup status..."
                : "Analyzing data..."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
            {personasToDisplay.map((persona) => {
              const creatorsStillLoadingForThisPersona =
                isPollingCreators &&
                !(matchedCreatorsMap[persona.id]?.top_creators?.length > 0);
              return (
                <RevPersonaCard
                  key={persona.id}
                  isButtonVariant
                  disabled={
                    creatorsStillLoadingForThisPersona || isPollingCreators
                  }
                  className={
                    creatorsStillLoadingForThisPersona || isPollingCreators
                      ? "!cursor-progress"
                      : ""
                  }
                  onClick={() => handlePersonaSelect(persona.id)}
                  onViewDetailsClick={() =>
                    setViewingPersona(persona as PersonaAPIResponseData)
                  }
                  selected={selectedPersona === persona.id}
                  persona={persona}
                />
              );
            })}
          </div>
        )}
      </RevCard>

      <div className="mt-6">
        <RevCard title="Creators" className="!px-4 !pb-4">
          {isPollingCreators && (
            <div className="flex items-center gap-2 mb-4">
              <p className="typography-body-sm text-monochrome-500">
                Analyzing 350M creators to find your best matches...
              </p>
              <RevSpinner color="primary" size="small" />
            </div>
          )}

          <div className="mb-8">
            <div className="flex gap-1 justify-between items-center mb-4">
              <span className="typography-body-bold-sm text-monochrome-900">
                Selected Creators
              </span>
              <span className="typography-body-xs text-monochrome-600">
                {selectedPairs.length} / 3 creators selected
              </span>
            </div>
            <div className="bg-primary-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                {[...Array(3)].map((_, index) => {
                  const selectedPair = selectedPairs[index];
                  const creator = selectedPair
                    ? allKnownCreators.find(
                        (creator) => creator.id === selectedPair.creatorId,
                      )
                    : null;

                  return creator ? (
                    <CreatorCard
                      key={creator.id}
                      data={creator}
                      handleSelect={() => {
                        const briefByCreator = briefs?.find(
                          (brief) => brief.creator.id === creator.id,
                        );
                        if (
                          briefByCreator &&
                          briefStatuses[briefByCreator.id] === "complete"
                        ) {
                          setIsDeleteDialogOpen(creator);
                        } else {
                          handleCreatorSelect(creator.id);
                        }
                      }}
                      selected
                      onboardingOpenDetails={() => setViewingCreator(creator)}
                    />
                  ) : (
                    <div
                      key={`placeholder-${index}`}
                      className="min-h-[191px] bg-monochrome-25 border border-dashed border-monochrome-200 rounded-lg flex flex-col items-center justify-center gap-4"
                    >
                      <div className="p-2.5 border border-monochrome-100 bg-monochrome-50 rounded-lg">
                        <FontAwesomeIcon
                          icon={faUser}
                          className="text-3xl text-monochrome-300"
                        />
                      </div>
                      <p className="typography-body-sm text-monochrome-500">
                        Creator #{index + 1}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4">
              <h6>Creators that match selected personas</h6>
              <span className="typography-body-xs text-monochrome-600">
                {selectedPersona ? `${availableCreators.length} creators` : ""}
              </span>
            </div>

            {isPollingCreators ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] gap-3 text-center">
                <RevSpinner color="primary" size="medium" centered />
                <p className="typography-body-sm text-monochrome-500">
                  Analyzing 350M creators to find your best matches...
                </p>
              </div>
            ) : selectedPersona && isFetchingCreatorsMap[selectedPersona] ? (
              <div className="flex items-center justify-center min-h-[200px]">
                <RevSpinner color="primary" size="medium" centered />
              </div>
            ) : !selectedPersona ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] gap-3 text-center">
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-2xl text-monochrome-600"
                />
                <p className="typography-body-sm text-monochrome-500">
                  Select a persona to see matched creators
                </p>
              </div>
            ) : availableCreators.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                {availableCreators.map((creator) => (
                  <CreatorCard
                    data={creator}
                    key={creator.id}
                    handleSelect={() => handleCreatorSelect(creator.id)}
                    maxSelectedCreators={selectedPairs.length === 3}
                    onboardingOpenDetails={() => setViewingCreator(creator)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[200px] gap-3 text-center">
                <p className="typography-body-sm text-monochrome-500">
                  No matching creators found for this persona.
                </p>
              </div>
            )}
          </div>
        </RevCard>
      </div>
      <RevDialog
        isOpen={!!isDeleteDialogOpen}
        setIsOpen={() => setIsDeleteDialogOpen(null)}
        title={`Delete ${isDeleteDialogOpen?.name}`}
        btnList={[
          <RevButton
            key="cancel"
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsDeleteDialogOpen(null)}
          />,
          <RevButton
            key="delete"
            text="Delete Creator"
            variant="primary"
            buttonColor="error"
            handleClick={() => {
              if (isDeleteDialogOpen) {
                handleCreatorSelect(isDeleteDialogOpen?.id);
                setIsDeleteDialogOpen(null);
              }
            }}
          />,
        ]}
      >
        <p className="typography-body-sm text-monochrome-900">
          Are you sure you want to delete the creator "
          {isDeleteDialogOpen?.name}
          "? A brief has already been generated for it, if you delete it, the
          brief will be deleted.
        </p>
      </RevDialog>
      <PersonaDetail
        personaId={viewingPersona?.id.toString() || null}
        setIsOpen={() => setViewingPersona(null)}
      />
      <CreatorOverview
        creatorId={viewingCreator?.id.toString() || null}
        setIsOpen={() => setViewingCreator(null)}
        menu={null}
      />
    </div>
  );
};
